import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { UserCog } from "lucide-react";

export type GuideServiceType = "half_day" | "full_day" | "airport_transfer" | "dinner_transfer";
export type GuideServiceMode = "guide_only" | "driver_guide";
export type GuideRateCycle = "current" | "next2027";

export type GuideRate = {
  id?: string;
  service_type: GuideServiceType;
  service_mode: GuideServiceMode;
  rate: number | null;
  currency: string;
  valid_from: string;
  valid_to: string;
  notes: string;
  is_active: boolean;
};

export const GUIDE_RATE_CYCLES: Record<GuideRateCycle, { label: string; valid_from: string; valid_to: string }> = {
  current: { label: "Current (until 30 Sep 2026)", valid_from: "2025-10-01", valid_to: "2026-09-30" },
  next2027: { label: "2027 Cycle (01 Oct 2026 – 30 Sep 2027)", valid_from: "2026-10-01", valid_to: "2027-09-30" },
};

const SERVICE_TYPES: { key: GuideServiceType; label: string }[] = [
  { key: "half_day", label: "Half Day Tour" },
  { key: "full_day", label: "Full Day Tour" },
  { key: "airport_transfer", label: "Airport Transfer" },
  { key: "dinner_transfer", label: "Dinner Transfer" },
];

const SERVICE_MODES: { key: GuideServiceMode; label: string }[] = [
  { key: "guide_only", label: "Guide Only" },
  { key: "driver_guide", label: "Driver-Guide" },
];

const CURRENCIES = ["ZAR", "USD", "EUR", "GBP"];

interface Props {
  rates: GuideRate[];
  onChange: (rates: GuideRate[]) => void;
}

export function SupplierGuideRatesSection({ rates, onChange }: Props) {
  const findRate = (cycle: GuideRateCycle, st: GuideServiceType, sm: GuideServiceMode) => {
    const c = GUIDE_RATE_CYCLES[cycle];
    return rates.find(
      (r) => r.valid_from === c.valid_from && r.valid_to === c.valid_to && r.service_type === st && r.service_mode === sm
    );
  };

  const upsertRate = (cycle: GuideRateCycle, st: GuideServiceType, sm: GuideServiceMode, updates: Partial<GuideRate>) => {
    const c = GUIDE_RATE_CYCLES[cycle];
    const idx = rates.findIndex(
      (r) => r.valid_from === c.valid_from && r.valid_to === c.valid_to && r.service_type === st && r.service_mode === sm
    );
    if (idx === -1) {
      onChange([
        ...rates,
        {
          service_type: st,
          service_mode: sm,
          rate: null,
          currency: "ZAR",
          valid_from: c.valid_from,
          valid_to: c.valid_to,
          notes: "",
          is_active: true,
          ...updates,
        },
      ]);
    } else {
      const next = [...rates];
      next[idx] = { ...next[idx], ...updates };
      onChange(next);
    }
  };

  // Get the currency for a whole cycle (first non-null row's currency, defaults ZAR)
  const getCycleCurrency = (cycle: GuideRateCycle) => {
    const c = GUIDE_RATE_CYCLES[cycle];
    const row = rates.find((r) => r.valid_from === c.valid_from && r.valid_to === c.valid_to);
    return row?.currency || "ZAR";
  };

  const setCycleCurrency = (cycle: GuideRateCycle, currency: string) => {
    const c = GUIDE_RATE_CYCLES[cycle];
    const next = rates.map((r) =>
      r.valid_from === c.valid_from && r.valid_to === c.valid_to ? { ...r, currency } : r
    );
    onChange(next);
  };

  // Single notes per cycle: store on any row for that cycle
  const getCycleNotes = (cycle: GuideRateCycle) => {
    const c = GUIDE_RATE_CYCLES[cycle];
    const row = rates.find((r) => r.valid_from === c.valid_from && r.valid_to === c.valid_to && r.notes);
    return row?.notes || "";
  };

  const setCycleNotes = (cycle: GuideRateCycle, notes: string) => {
    const c = GUIDE_RATE_CYCLES[cycle];
    const anyRow = rates.some((r) => r.valid_from === c.valid_from && r.valid_to === c.valid_to);
    if (!anyRow) {
      // create a placeholder row on first service type/mode
      upsertRate(cycle, "half_day", "guide_only", { notes });
      return;
    }
    const next = rates.map((r) =>
      r.valid_from === c.valid_from && r.valid_to === c.valid_to ? { ...r, notes } : r
    );
    onChange(next);
  };

  const renderMatrix = (cycle: GuideRateCycle) => {
    const currency = getCycleCurrency(cycle);
    return (
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Enter per-service rates. Leave blank if not offered.
            </p>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Currency</Label>
              <Select value={currency} onValueChange={(v) => setCycleCurrency(cycle, v)}>
                <SelectTrigger className="h-8 w-24"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-3 font-medium text-muted-foreground">Service</th>
                  {SERVICE_MODES.map((m) => (
                    <th key={m.key} className="py-2 px-2 font-medium text-muted-foreground text-right">{m.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SERVICE_TYPES.map((st) => (
                  <tr key={st.key} className="border-b last:border-b-0">
                    <td className="py-2 pr-3">{st.label}</td>
                    {SERVICE_MODES.map((m) => {
                      const r = findRate(cycle, st.key, m.key);
                      return (
                        <td key={m.key} className="py-2 px-2">
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            value={r?.rate ?? ""}
                            onChange={(e) =>
                              upsertRate(cycle, st.key, m.key, {
                                rate: e.target.value ? parseFloat(e.target.value) : null,
                                currency,
                              })
                            }
                            placeholder="—"
                            className="text-right"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Notes for this cycle</Label>
            <Textarea
              value={getCycleNotes(cycle)}
              onChange={(e) => setCycleNotes(cycle, e.target.value)}
              placeholder="e.g., Rates include VAT, exclude park fees"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <UserCog className="h-5 w-5 text-muted-foreground" />
        <Label className="text-base font-semibold">Guide Rates</Label>
      </div>
      <Tabs defaultValue="current" className="w-full">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="current">{GUIDE_RATE_CYCLES.current.label}</TabsTrigger>
          <TabsTrigger value="next2027">{GUIDE_RATE_CYCLES.next2027.label}</TabsTrigger>
        </TabsList>
        <TabsContent value="current" className="mt-4">{renderMatrix("current")}</TabsContent>
        <TabsContent value="next2027" className="mt-4">{renderMatrix("next2027")}</TabsContent>
      </Tabs>
    </div>
  );
}